import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Col, Divider, Empty, Form, Input, message, Modal, InputNumber, Row, Select, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useCourseList } from 'PlatForms/OrgLMS/Hooks/AdminHooks/useCourseList';
import api from 'Common/Client/Interceptor';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;
const CourseList = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [availableSkills, setAvailableSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [expandedSkills, setExpandedSkills] = useState({});
  const [selectedBusinessGroup, setSelectedBusinessGroup] = useState(null);
  const {
    courses,
    filteredCourses,
    businessGroups,
    loading,
    submitLoading,
    selectedGroup,
    filterCourses,
    AddCourse,
    setCourses
  } = useCourseList();
  const handleBusinessGroupChange = (value) => {
    filterCourses(value);
  };
  const handleModalBusinessGroupSelect = (value) => {
    setSelectedBusinessGroup(value);
    form.setFieldsValue({ businessGroupId: value });
    // Clear skills when business group changes
    setSelectedSkills([]);
    form.setFieldsValue({ skills: [] });
    if (value) {
      const fetchSkills = async () => {
        try {
          const response = await api.get(`${process.env.REACT_APP_API_URL}/Skills/businessgroup/${value}/Lookup`);
          setAvailableSkills(response.data);
        } catch (error) {
          console.error('Error fetching skills:', error);
          message.error('Failed to load skills for selected business group');
        }
      };
      fetchSkills();
    } else {
      setAvailableSkills([]);
    }
  };
  // Prevent deletion of skills with backspace
  const handleSkillKeyDown = (e) => {
    if (e.key === 'Backspace' && !e.target.value) {
      e.preventDefault();
    }
  };
  const toggleSkills = (courseId) => {
    setExpandedSkills(prev => ({
      ...prev,
      [courseId]: !prev[courseId]
    }));
  };
  const handleAddCourse = () => {
    setModalVisible(true);
    setSelectedSkills([]);
    setSelectedBusinessGroup(null);
  };
  const handleModalCancel = () => {
    form.resetFields();
    setModalVisible(false);
    setSelectedBusinessGroup(null);
    setSelectedSkills([]);
  };
  const handleSubmit = async (values) => {
    try {
      if (!selectedBusinessGroup) {
        message.error('Please select a business group first');
        return;
      }
      const courseData = {
        title: values.title,
        description: values.description,
        duration: values.duration,
        businessGroupId: selectedBusinessGroup,
        skills: selectedSkills.filter(skill => typeof skill === 'number'), // Only numeric skill IDs
        customSkills: selectedSkills.filter(skill => typeof skill === 'string') // Only string custom skills
      };
      const newCourse = await AddCourse(courseData);
      const updatedCourses = [...courses, newCourse];
      setCourses(updatedCourses);
      filterCourses(selectedGroup);
      message.success("Course added successfully!");
      form.resetFields();
      setModalVisible(false);
      navigate(`/course-detail/${newCourse.id}`);
    } catch (error) {
      console.error("Full error details:", error.response?.data || error);
      message.error(
        error.response?.data?.message ||
        error.response?.data?.title ||
        "Failed to add course"
      );
    }
  };
  return (
    <Card
      className="card-professional"
      loading={loading}
      title="Training Courses"
      extra={
        <Button type="default" onClick={() => navigate("/training-library")}>
          Back
        </Button>
      }
    >
      <Row align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCourse}>
            Add Course
          </Button>
        </Col>
        <Col>
          <Form.Item style={{ marginBottom: 0, marginLeft: 8 }}>
            <Select
              showSearch
              placeholder="Filter by Business Group"
              style={{ width: 220 }}
              value={selectedGroup}
              allowClear
              onChange={handleBusinessGroupChange}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {businessGroups.map((group) => (
                <Option key={group.id} value={group.id}>{group.name}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Modal
        title="Add New Course"
        open={modalVisible}
        onCancel={handleModalCancel}
        footer={[
          <Button key="back" onClick={handleModalCancel}>Cancel</Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitLoading}
            onClick={() => form.submit()}
            disabled={!selectedBusinessGroup || selectedSkills.length === 0}
          >
            Submit
          </Button>,
        ]}
        centered
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Title"
            name="title"
            rules={[
              { required: true, message: "Please enter course title!" },
              { max: 100, message: "Title cannot exceed 100 characters!" },
            ]}
          >
            <Input placeholder="Enter course title" maxLength={100} showCount />
          </Form.Item>
          <Form.Item
            label="Business Group"
            name="businessGroupId"
            rules={[{ required: true, message: "Please select a business group!" }]}
          >
            <Select
              placeholder="Select Business Group"
              showSearch
              optionFilterProp="children"
              onChange={handleModalBusinessGroupSelect}
              value={selectedBusinessGroup}
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {businessGroups.map((group) => (
                <Option key={group.id} value={group.id}>{group.name}</Option>
              ))}
            </Select>
          </Form.Item>
          {selectedBusinessGroup && (
            <Form.Item
              label="Required Skills"
              name="skills"
              rules={[{ required: true, message: "Please select at least one skill!" }]}
            >
              <Select
                mode="tags"
                placeholder="Select skills or type to add new"
                value={selectedSkills}
                onChange={(value) => {
                  setSelectedSkills(value);
                  form.setFieldsValue({ skills: value });
                }}
                onInputKeyDown={handleSkillKeyDown}
                tokenSeparators={[',']}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                showSearch
              >
                {availableSkills.map(skill => (
                  <Option key={skill.id} value={skill.id}>
                    {skill.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="Duration (in Days)"
            name="duration"
            rules={[
              { required: true, message: "Please enter duration in days!" },
              {
                type: "number",
                min: 1,
                max: 365,
                message: "Duration must be between 1 and 365 days",
              },
            ]}
          >
            <InputNumber
              placeholder="Enter duration in days (1-365)"
              min={1}
              max={365}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: "Please enter course description!" },
              { max: 500, message: "Description cannot exceed 500 characters!" },
            ]}
          >
            <TextArea placeholder="Enter course description" rows={4} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
      {/* Courses List */}
      <Row gutter={[16, 16]}>
        {filteredCourses.length > 0 ? (
          filteredCourses.map((course) => (
            <Col key={course.id} xs={24} sm={12} md={12}>
              <Card
                variant="borderless"
                style={{
                  height: 300,
                  borderRadius: 12,
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                  padding: "16px",
                  background: course.archived ? "rgb(224 235 235)" : "#ffffff",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, minHeight: 40 }}>
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => navigate(`/course-detail/${course.id}`)}
                  >
                    Content
                  </Button>
                </div>

                <p style={{ marginBottom: 8, wordBreak: "break-word", color: course.archived ? "#8C6400" : "#9A6B0E" }}>
                  <strong>{course.title}</strong>
                </p>

                <div style={{ maxHeight: 140, overflowY: "auto", paddingRight: 8 }}>
                  <Text style={{ display: "block", color: "#595959", fontSize: "13px", lineHeight: 1.4 }}>
                    {course.description || "N/A"}
                  </Text>

                  {course.courseSkills?.length > 0 ? (
                    <>
                      <Divider orientation="left" style={{ margin: '8px 0', fontSize: '12px' }}>
                        Skills
                        {course.courseSkills.length > 3 && (
                          <Button
                            type="link"
                            size="small"
                            onClick={() => toggleSkills(course.id)}
                            style={{ marginLeft: 8 }}
                          >
                            {expandedSkills[course.id] ? 'Show Less' : `+${course.courseSkills.length - 3} more`}
                          </Button>
                        )}
                      </Divider>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                        {(expandedSkills[course.id] ? course.courseSkills : course.courseSkills.slice(0, 3)).map((skill) => (
                          <Tag
                            key={skill.id}
                            color="#9A6B0Eff"
                            style={{ margin: 0, fontSize: '12px' }}
                          >
                            {skill.name}
                          </Tag>
                        ))}
                      </div>
                    </>
                  ) : (
                    <Text type="secondary">No skills data available</Text>
                  )}

                  <div style={{ marginTop: 8 }}>
                    <Text strong style={{ color: "#333", fontSize: "13px" }}>Created on:</Text>
                    <Text style={{ display: "block", color: "#595959", fontSize: "13px" }}>
                      {new Date(course.createdOn).toLocaleString()}
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
          ))
        ) : (
          <Col span={24} style={{ textAlign: "center", padding: "20px" }}>
            <Empty description="No courses available" />
          </Col>
        )}
      </Row>
    </Card>
  );
};
export default CourseList;